
import React, { useState } from 'react';
import { 
  Save, 
  Loader2, 
  CheckCircle, 
  ChevronLeft, 
  Shield, 
  ChevronUp,
  Sun,
  Moon,
  Monitor,
  Settings as SettingsIcon,
  User,
  Globe
} from 'lucide-react';
import { UserProfile, AppLanguage, ChatbotPersonality, AppTheme } from '../types';

interface ProfileViewProps {
  user: UserProfile;
  language: AppLanguage;
  onUpdate: (updates: Partial<UserProfile>) => void;
  onBack?: () => void;
}

const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇺🇸' },
  { code: 'zo', name: 'Zomi', native: 'Zopau', flag: '🏳️' },
  { code: 'my', name: 'Burmese', native: 'ဗမာစာ', flag: '🇲🇲' }
];

const ProfileView: React.FC<ProfileViewProps> = ({ user, language, onUpdate, onBack }) => {
  const [username, setUsername] = useState(user.username || '');
  const [email, setEmail] = useState(user.email || '');
  const [prefPersona, setPrefPersona] = useState<ChatbotPersonality>(user.personality);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleSave = (e?: React.FormEvent) => {
    e?.preventDefault();
    setIsSaving(true);
    
    setTimeout(() => {
      onUpdate({
        username,
        email,
        language: user.language,
        personality: prefPersona,
        theme: user.theme
      });
      setIsSaving(false);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    }, 800);
  };

  const onThemeChange = (theme: AppTheme) => {
    onUpdate({ theme });
  };

  const onLanguageChange = (lang: AppLanguage) => {
    onUpdate({ language: lang });
  };

  return (
    <div className="w-full h-full flex flex-col bg-white dark:bg-[#131314] overflow-y-auto custom-scrollbar transition-colors duration-300">
      {/* Settings Header */}
      <div className="sticky top-0 z-30 bg-white/90 dark:bg-[#131314]/90 backdrop-blur-xl border-b border-slate-200 dark:border-white/[0.05] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button 
            onClick={onBack}
            className="p-2 -ml-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded-full text-slate-400 dark:text-white/40 hover:text-slate-900 dark:hover:text-[#e3e3e3] transition-all active:scale-90"
          >
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-slate-900 dark:text-[#e3e3e3]">Settings</h1>
        </div>
        
        <button
          onClick={() => handleSave()}
          disabled={isSaving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-2 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-50 shadow-lg shadow-blue-500/10"
        >
          {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {isSaving ? 'Saving' : 'Save'}
        </button>
      </div>

      <div className="max-w-xl mx-auto w-full px-6 py-8 space-y-10 pb-32">
        
        {/* Success Alert */}
        {showSuccess && (
          <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-2xl flex items-center gap-3 text-green-600 dark:text-green-400 animate-in fade-in slide-in-from-top-2 duration-300">
            <CheckCircle size={20} />
            <p className="text-sm font-bold">Preferences updated successfully</p>
          </div>
        )}

        {/* Visual Controls Container */}
        <section className="space-y-4">
          <div className="flex items-center gap-2 text-slate-400 dark:text-[#b4b4b4]/30 ml-1">
             <SettingsIcon size={14} />
             <h3 className="text-[10px] font-black uppercase tracking-widest">Interface Appearance</h3>
          </div>
          
          <div className="bg-slate-100/50 dark:bg-[#1e1f20] p-6 rounded-[2.5rem] space-y-8 border border-slate-200 dark:border-white/[0.05]">
            {/* Theme Toggle Pill */}
            <div className="flex items-center bg-white dark:bg-[#2e2f30] p-1.5 rounded-2xl shadow-sm border border-slate-100 dark:border-white/5">
               <button onClick={() => onThemeChange('light')} className={`flex-1 flex items-center justify-center py-3 rounded-xl transition-all ${user.theme === 'light' ? 'bg-slate-50 dark:bg-[#444] text-blue-600 dark:text-blue-400 shadow-md ring-1 ring-black/5' : 'text-slate-400 hover:text-slate-600 dark:hover:text-[#e3e3e3]'}`} title="Light Mode"><Sun size={18} /></button>
               <button onClick={() => onThemeChange('dark')} className={`flex-1 flex items-center justify-center py-3 rounded-xl transition-all ${user.theme === 'dark' ? 'bg-slate-50 dark:bg-[#444] text-blue-600 dark:text-blue-400 shadow-md ring-1 ring-black/5' : 'text-slate-400 hover:text-slate-600 dark:hover:text-[#e3e3e3]'}`} title="Dark Mode"><Moon size={18} /></button>
               <button onClick={() => onThemeChange('system')} className={`flex-1 flex items-center justify-center py-3 rounded-xl transition-all ${user.theme === 'system' ? 'bg-slate-50 dark:bg-[#444] text-blue-600 dark:text-blue-400 shadow-md ring-1 ring-black/5' : 'text-slate-400 hover:text-slate-600 dark:hover:text-[#e3e3e3]'}`} title="System Default"><Monitor size={18} /></button>
            </div>

            {/* Redesigned Language Selection */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-slate-400 dark:text-[#b4b4b4]/30 mb-2">
                <Globe size={12} />
                <span className="text-[10px] font-black uppercase tracking-widest">System Language</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {LANGUAGES.map((lang) => (
                  <button 
                    key={lang.code}
                    onClick={() => onLanguageChange(lang.code as AppLanguage)} 
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all border text-center group ${
                      user.language === lang.code 
                      ? 'bg-blue-600 text-white border-blue-600 shadow-lg scale-[1.02]' 
                      : 'bg-white dark:bg-[#2e2f30] text-slate-600 dark:text-[#b4b4b4] hover:bg-slate-50 dark:hover:bg-[#3e3f40] border-slate-100 dark:border-white/5 shadow-sm'
                    }`}
                  >
                    <span className="text-2xl mb-2 group-hover:scale-110 transition-transform">{lang.flag}</span>
                    <span className="text-[11px] font-black uppercase tracking-wider mb-0.5">{lang.name}</span>
                    <span className={`text-[10px] font-medium opacity-50 ${user.language === lang.code ? 'text-white' : ''}`}>{lang.native}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Profile Identity Card */}
            <div className="flex items-center gap-4 w-full p-5 rounded-3xl bg-white dark:bg-[#2e2f30] border border-slate-100 dark:border-white/5 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-700 to-indigo-600 flex items-center justify-center text-lg font-black text-white shadow-md flex-shrink-0">
                {username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-base font-bold text-slate-900 dark:text-[#e3e3e3] truncate leading-tight mb-0.5">{username || 'User Account'}</span>
                <span className="text-[10px] text-blue-600 dark:text-blue-400 uppercase font-black tracking-widest">{user.isPro ? 'PRO SUBSCRIPTION' : 'FREE ACCOUNT'}</span>
              </div>
              <ChevronUp size={20} className="text-slate-300 rotate-180" />
            </div>
          </div>
        </section>

        {/* General Form Settings Section */}
        <section className="space-y-6 pt-2">
          <div className="flex items-center gap-2 text-slate-400 dark:text-[#b4b4b4]/30 ml-1">
            <User size={14} />
            <h3 className="text-[10px] font-black uppercase tracking-widest">Account Details</h3>
          </div>

          <div className="space-y-5">
            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 dark:text-[#b4b4b4]/50 ml-1">Display Name</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-slate-50 dark:bg-[#1e1f20] border border-slate-200 dark:border-white/[0.05] text-slate-900 dark:text-[#e3e3e3] rounded-2xl py-4 px-5 focus:outline-none focus:border-blue-600/50 dark:focus:border-blue-500/50 transition-all text-[15px] font-semibold shadow-sm placeholder:text-slate-300 dark:placeholder:text-white/5"
                placeholder="Name"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 dark:text-[#b4b4b4]/50 ml-1">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-slate-50 dark:bg-[#1e1f20] border border-slate-200 dark:border-white/[0.05] text-slate-900 dark:text-[#e3e3e3] rounded-2xl py-4 px-5 focus:outline-none focus:border-blue-600/50 dark:focus:border-blue-500/50 transition-all text-[15px] font-semibold shadow-sm placeholder:text-slate-300 dark:placeholder:text-white/5"
                placeholder="Email"
              />
            </div>

            <div className="space-y-2">
              <label className="text-xs font-bold text-slate-500 dark:text-[#b4b4b4]/50 ml-1">AI Personality</label>
              <div className="relative">
                <select
                  value={prefPersona}
                  onChange={(e) => setPrefPersona(e.target.value as ChatbotPersonality)}
                  className="w-full bg-slate-50 dark:bg-[#1e1f20] border border-slate-200 dark:border-white/[0.05] text-slate-900 dark:text-[#e3e3e3] rounded-2xl py-4 px-5 focus:outline-none focus:border-blue-600/50 dark:focus:border-blue-500/50 transition-all text-[15px] font-semibold appearance-none cursor-pointer shadow-sm"
                >
                  <option value="professional">Professional</option>
                  <option value="friendly">Friendly</option>
                  <option value="creative">Creative</option>
                  <option value="concise">Concise</option>
                </select>
                <ChevronUp className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none rotate-180" size={18} />
              </div>
            </div>
          </div>
        </section>

        {/* Data Management Section */}
        <section className="pt-8 border-t border-slate-100 dark:border-white/[0.05] space-y-4">
          <div className="flex items-center gap-2 text-slate-400 dark:text-[#b4b4b4]/30 ml-1">
            <Shield size={14} />
            <h3 className="text-[10px] font-black uppercase tracking-widest">Privacy & Data</h3>
          </div>
          
          <div className="flex items-center justify-between p-6 bg-red-500/5 border border-red-500/10 rounded-[2.5rem]">
            <div>
              <p className="text-sm font-black text-red-600 dark:text-red-500 mb-1">Clear Local History</p>
              <p className="text-xs text-slate-400 dark:text-[#b4b4b4]/20 font-medium">Reset your local cache and chat sessions.</p>
            </div>
            <button className="px-5 py-2.5 bg-white dark:bg-[#2e2f30] text-red-600 dark:text-red-500 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 border border-red-600/20 hover:bg-red-600 hover:text-white">
              Delete All
            </button>
          </div>
        </section>
      </div>

      {/* Footer Branding */}
      <div className="mt-auto py-12 text-center">
        <p className="text-[10px] text-slate-200 dark:text-[#b4b4b4]/10 font-black tracking-[0.8em] select-none uppercase">
          Intelligence Architecture • V3.1.2
        </p>
      </div>
    </div>
  );
};

export default ProfileView;
